import React, { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import api, { extractError } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { PageHeader, LoadingState, EmptyState } from "@/components/Common";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { personName } from "@/lib/format";
import { CLASSIFICATIONS } from "@/lib/constants";
import { Users, Search, UserPlus, Phone, Mail, AlertTriangle, Loader2 } from "lucide-react";
import { VoiceSearchButton, appendSearch } from "@/components/VoiceSearchButton";
import { toast } from "sonner";

const emptyForm = { nome: "", cognome: "", ragione_sociale: "", telefono_principale: "", email: "", codice_fiscale: "", citta: "", provincia: "", indirizzo: "", numero_civico: "", classificazioni: [] };

const Persons = () => {
  const { can } = useAuth();
  const navigate = useNavigate();
  const [items, setItems] = useState([]);
  const [total, setTotal] = useState(0);
  const [q, setQ] = useState("");
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(emptyForm);
  const [dups, setDups] = useState([]);
  const [saving, setSaving] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try { const { data } = await api.get(`/persons?q=${encodeURIComponent(q)}&limit=50`); setItems(data.items); setTotal(data.total); } catch (e) { console.error("Errore caricamento persone", e); }
    finally { setLoading(false); }
  }, [q]);
  useEffect(() => { const t = setTimeout(load, 250); return () => clearTimeout(t); }, [load]);

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const save = async (force = false) => {
    if (!form.cognome && !form.ragione_sociale) { toast.error("Inserisci cognome o ragione sociale"); return; }
    setSaving(true);
    try {
      const { data } = await api.post(`/persons?force=${force}`, form);
      toast.success("Scheda persona creata");
      setOpen(false); setForm(emptyForm); setDups([]);
      navigate(`/app/persone/${data.id}`);
    } catch (e) {
      const detail = e?.response?.data?.detail;
      if (e?.response?.status === 409 && detail?.potenziali_duplicati) {
        setDups(detail.potenziali_duplicati);
        toast.warning("Possibili duplicati trovati. Verifica prima di continuare.");
      } else { toast.error(extractError(e)); }
    } finally { setSaving(false); }
  };

  return (
    <div>
      <PageHeader icon={Users} title="Persone" description={`${total} schede · anagrafica unica della Rete`}
        actions={can("persons.write") && <Button onClick={() => { setForm(emptyForm); setDups([]); setOpen(true); }} className="bg-[color:var(--brand-orange-600)] hover:bg-[color:var(--brand-orange-500)] text-white" data-testid="new-person-button"><UserPlus className="h-4 w-4 mr-2" /> Nuova persona</Button>} />

      <div className="relative mb-4 max-w-md">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
        <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Cerca per nome, telefono, email, CF…" className="pl-9 pr-11 h-11" data-testid="persons-search" />
        <div className="absolute right-2 top-1/2 -translate-y-1/2">
          <VoiceSearchButton onResult={(t) => setQ((p) => appendSearch(p, t))} data-testid="persons-search-mic" />
        </div>
      </div>

      {loading ? <LoadingState /> : items.length === 0 ? <EmptyState title="Nessuna persona trovata" description="Prova a modificare la ricerca oppure crea una nuova scheda." /> : (
        <Card className="divide-y">
          {items.map((p) => (
            <button key={p.id} onClick={() => navigate(`/app/persone/${p.id}`)} className="w-full text-left p-4 hover:bg-muted transition-colors flex items-center gap-3" data-testid="person-row">
              <div className="h-10 w-10 rounded-full bg-[color:var(--brand-blue-600)] text-white flex items-center justify-center text-sm font-semibold shrink-0">
                {personName(p).split(" ").slice(0,2).map(s=>s[0]).join("")}
              </div>
              <div className="min-w-0 flex-1">
                <div className="font-medium">{personName(p)}</div>
                <div className="text-xs text-muted-foreground flex flex-wrap gap-x-3">
                  {p.telefono_principale && <span className="inline-flex items-center gap-1"><Phone className="h-3 w-3" />{p.telefono_principale}</span>}
                  {p.email && <span className="inline-flex items-center gap-1"><Mail className="h-3 w-3" />{p.email}</span>}
                  {p.citta && <span>{p.citta} ({p.provincia})</span>}
                </div>
              </div>
              <div className="flex gap-1 flex-wrap justify-end">
                {(p.classificazioni||[]).slice(0,2).map(c=><span key={c} className="text-[10px] rounded-full bg-slate-100 px-2 py-0.5">{c}</span>)}
              </div>
            </button>
          ))}
        </Card>
      )}

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-auto ras-scroll">
          <DialogHeader><DialogTitle className="font-display">Nuova persona</DialogTitle></DialogHeader>
          {dups.length > 0 && (
            <div className="rounded-lg border border-[color:var(--brand-orange-500)] bg-[color:var(--brand-orange-100)] p-3">
              <div className="flex items-center gap-2 font-medium text-sm text-[color:var(--brand-orange-600)]"><AlertTriangle className="h-4 w-4" /> Potrebbe essere già presente una persona con dati simili. Verifica prima di creare una nuova scheda.</div>
              <div className="mt-2 space-y-1">
                {dups.map((d) => (
                  <button key={d.id} onClick={() => navigate(`/app/persone/${d.id}`)} className="block w-full text-left text-sm rounded border bg-white px-2 py-1.5 hover:bg-muted">
                    {personName(d)} · {d.telefono_principale} {d.email ? `· ${d.email}` : ""}
                  </button>
                ))}
              </div>
            </div>
          )}
          <div className="grid sm:grid-cols-2 gap-3">
            <div><Label>Cognome</Label><Input value={form.cognome} onChange={(e) => set("cognome", e.target.value)} data-testid="person-cognome-input" /></div>
            <div><Label>Nome</Label><Input value={form.nome} onChange={(e) => set("nome", e.target.value)} /></div>
            <div><Label>Ragione sociale (persona giuridica)</Label><Input value={form.ragione_sociale} onChange={(e) => set("ragione_sociale", e.target.value)} /></div>
            <div><Label>Codice fiscale</Label><Input value={form.codice_fiscale} onChange={(e) => set("codice_fiscale", e.target.value)} /></div>
            <div><Label>Telefono principale</Label><Input value={form.telefono_principale} onChange={(e) => set("telefono_principale", e.target.value)} data-testid="person-telefono-input" /></div>
            <div><Label>Email</Label><Input value={form.email} onChange={(e) => set("email", e.target.value)} /></div>
            <div><Label>Indirizzo</Label><Input value={form.indirizzo} onChange={(e) => set("indirizzo", e.target.value)} /></div>
            <div><Label>N. civico</Label><Input value={form.numero_civico} onChange={(e) => set("numero_civico", e.target.value)} /></div>
            <div><Label>Città</Label><Input value={form.citta} onChange={(e) => set("citta", e.target.value)} /></div>
            <div><Label>Provincia</Label><Input value={form.provincia} onChange={(e) => set("provincia", e.target.value)} maxLength={2} /></div>
            <div className="sm:col-span-2"><Label>Classificazione</Label>
              <Select onValueChange={(v) => set("classificazioni", [v])}>
                <SelectTrigger><SelectValue placeholder="Seleziona" /></SelectTrigger>
                <SelectContent className="max-h-56">{CLASSIFICATIONS.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}</SelectContent>
              </Select></div>
          </div>
          <DialogFooter className="gap-2">
            <Button variant="outline" onClick={() => setOpen(false)}>Annulla</Button>
            <Button onClick={() => save(dups.length > 0)} disabled={saving} className="bg-[color:var(--brand-orange-600)] hover:bg-[color:var(--brand-orange-500)] text-white" data-testid="person-save-button">
              {saving && <Loader2 className="h-4 w-4 animate-spin mr-2" />}{dups.length > 0 ? "Crea comunque" : "Salva"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default Persons;
